import React from 'react';
import { Helmet } from 'react-helmet';
import { Shield, Lock, Eye, FileText, Mail, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - NextGen Filling Association | Data Protection & Security</title>
        <meta name="description" content="Read our privacy policy to understand how NextGen Filling Association collects, uses, and protects your personal information and business data." />
        <meta name="keywords" content="privacy policy, data protection, information security, confidentiality, GDPR compliance" />
        <link rel="canonical" href="https://nextgenfilling.com/privacy-policy" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Header Section */}
        <section className="bg-gradient-to-r from-slate-900 to-blue-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-16 h-16 text-amber-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Privacy Policy
            </h1>
            <p className="text-lg text-center text-slate-300 max-w-3xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
              Your privacy is important to us. This policy outlines how we collect, use, and protect your personal information.
            </p>
            <p className="text-sm text-center text-slate-400 mt-4">
              Last Updated: February 28, 2025
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
              
              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <div className="flex items-start gap-4 mb-6">
                  <FileText className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      1. Introduction
                    </h2>
                    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                      NextGen Filling Association ("we," "us," or "our") is committed to protecting your privacy and personal information. 
                      This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                      or use our services for company registration, GST filing, trademark registration, and other business services.
                    </p>
                  </div>
                </div>
              </div>

              {/* Information We Collect */}
              <div className="prose prose-lg max-w-none">
                <div className="flex items-start gap-4 mb-6">
                  <Eye className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      2. Information We Collect
                    </h2>
                    
                    <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.1 Personal Information</h3>
                    <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                      We collect personal information that you voluntarily provide to us when you:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                      <li>Register for our services</li>
                      <li>Fill out contact forms or inquiry forms</li>
                      <li>Subscribe to newsletters or updates</li>
                      <li>Communicate with us via email, phone, or chat</li>
                      <li>Use our live chat feature</li>
                    </ul>
                    
                    <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">2.2 Types of Data Collected</h3>
                    <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                      <li><strong>Identity Data:</strong> Name, date of birth, gender</li>
                      <li><strong>Contact Data:</strong> Email address, phone number, postal address</li>
                      <li><strong>Business Data:</strong> Company name, business type, PAN, GST number, director details</li>
                      <li><strong>Financial Data:</strong> Bank account details (for service payments)</li>
                      <li><strong>Technical Data:</strong> IP address, browser type, device information, cookies</li>
                      <li><strong>Usage Data:</strong> Information about how you use our website and services</li>
                      <li><strong>Document Data:</strong> Identification documents, business documents for registration purposes</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div className="prose prose-lg max-w-none">
                <div className="flex items-start gap-4 mb-6">
                  <Lock className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      3. How We Use Your Information
                    </h2>
                    <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                      We use the information we collect for the following purposes:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                      <li><strong>Service Delivery:</strong> To provide company registration, GST filing, trademark registration, and other professional services</li>
                      <li><strong>Communication:</strong> To respond to your inquiries, send service updates, and provide customer support</li>
                      <li><strong>Legal Compliance:</strong> To file documents with government authorities (MCA, GST department, trademark office, etc.)</li>
                      <li><strong>Account Management:</strong> To create and manage your account with us</li>
                      <li><strong>Payment Processing:</strong> To process payments for our services</li>
                      <li><strong>Marketing:</strong> To send promotional materials (only with your consent)</li>
                      <li><strong>Website Improvement:</strong> To analyze website usage and improve user experience</li>
                      <li><strong>Security:</strong> To detect and prevent fraud, unauthorized access, and other illegal activities</li>
                      <li><strong>Legal Obligations:</strong> To comply with applicable laws and regulations</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Information Sharing */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  4. Information Sharing and Disclosure
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We do not sell or rent your personal information to third parties. We may share your information with:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li><strong>Government Authorities:</strong> Ministry of Corporate Affairs (MCA), GST Department, Trademark Registry, and other regulatory bodies as required for service delivery</li>
                  <li><strong>Service Providers:</strong> Third-party vendors who assist us in providing services (payment processors, email service providers, hosting providers)</li>
                  <li><strong>Professional Advisors:</strong> Lawyers, auditors, and consultants as needed</li>
                  <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulations</li>
                </ul>
              </div>

              {/* Data Security */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  5. Data Security
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We implement appropriate technical and organizational security measures to protect your personal information, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>SSL encryption for data transmission</li>
                  <li>Secure servers with firewall protection</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Regular security audits and updates</li>
                  <li>Employee training on data protection</li>
                  <li>Confidentiality agreements with staff and partners</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, 
                  we cannot guarantee absolute security.
                </p>
              </div>

              {/* Data Retention */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  6. Data Retention
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, 
                  unless a longer retention period is required by law. Specifically:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-3">
                  <li><strong>Active Client Data:</strong> Retained for the duration of our service relationship</li>
                  <li><strong>Completed Projects:</strong> Retained for 7 years as per legal and accounting requirements</li>
                  <li><strong>Marketing Data:</strong> Retained until you opt-out of communications</li>
                  <li><strong>Legal Documents:</strong> Retained as per applicable laws (Companies Act, GST Act, etc.)</li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  7. Your Rights
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  You have the following rights regarding your personal information:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li><strong>Right to Access:</strong> Request copies of your personal data</li>
                  <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong>Right to Erasure:</strong> Request deletion of your personal data (subject to legal obligations)</li>
                  <li><strong>Right to Restrict Processing:</strong> Request limitation on how we use your data</li>
                  <li><strong>Right to Data Portability:</strong> Request transfer of your data to another service provider</li>
                  <li><strong>Right to Object:</strong> Object to processing of your personal data for marketing purposes</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time (where processing is based on consent)</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                  To exercise any of these rights, please contact us using the information provided below.
                </p>
              </div>

              {/* Cookies */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  8. Cookies and Tracking Technologies
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We use cookies and similar tracking technologies to enhance your experience on our website:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                  <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  You can control cookies through your browser settings. However, disabling cookies may affect website functionality.
                </p>
              </div>

              {/* Third-Party Links */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  9. Third-Party Links
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of these 
                  external sites. We encourage you to review their privacy policies before providing any personal information.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  10. Children's Privacy
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal 
                  information from children. If you believe we have collected information from a child, please contact us immediately.
                </p>
              </div>

              {/* Changes to Policy */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  11. Changes to This Privacy Policy
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
                  Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this policy 
                  periodically for any changes.
                </p>
              </div>

              {/* Contact Information */}
              <div className="bg-gradient-to-br from-blue-50 to-amber-50 rounded-xl p-8 mt-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  12. Contact Us
                </h2>
                <p className="text-slate-700 leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-semibold text-slate-900">Email</p>
                      <a href="mailto:info@nextgenfilling.com" className="text-blue-600 hover:text-blue-800">
                        info@nextgenfilling.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-semibold text-slate-900">Phone</p>
                      <p className="text-slate-700">+91 9509363261, +91 9541709908</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Consent */}
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6">
                <p className="text-slate-800 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  By using our website and services, you acknowledge that you have read and understood this Privacy Policy 
                  and agree to the collection, use, and disclosure of your information as described herein.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;
